(() => {
  const STORAGE_KEY = 'americanStrideChatPositionV1';
  const EDGE_GAP = 8;
  const DRAG_THRESHOLD = 6;
  const initializedButtons = new WeakSet();
  const observedRoots = new WeakSet();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

  const readPosition = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Number.isFinite(value?.x) && Number.isFinite(value?.y)) return value;
    } catch (_error) {
      // Storage can be unavailable in private browsing modes.
    }
    return null;
  };

  const savePosition = (left, top, width, height) => {
    const availableWidth = Math.max(1, window.innerWidth - width - EDGE_GAP * 2);
    const availableHeight = Math.max(1, window.innerHeight - height - EDGE_GAP * 2);

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          x: clamp((left - EDGE_GAP) / availableWidth, 0, 1),
          y: clamp((top - EDGE_GAP) / availableHeight, 0, 1),
        })
      );
    } catch (_error) {
      // Dragging still works for the current page when storage is unavailable.
    }
  };

  const placeWrapper = (wrapper, left, top, width, height) => {
    wrapper.style.position = 'fixed';
    wrapper.style.left = `${clamp(left, EDGE_GAP, window.innerWidth - width - EDGE_GAP)}px`;
    wrapper.style.top = `${clamp(top, EDGE_GAP, window.innerHeight - height - EDGE_GAP)}px`;
    wrapper.style.right = 'auto';
    wrapper.style.bottom = 'auto';
    wrapper.style.margin = '0';
    wrapper.style.transform = 'none';
  };

  const restorePosition = (wrapper, button) => {
    const position = readPosition();
    if (!position) return;

    const rect = button.getBoundingClientRect();
    const availableWidth = Math.max(1, window.innerWidth - rect.width - EDGE_GAP * 2);
    const availableHeight = Math.max(1, window.innerHeight - rect.height - EDGE_GAP * 2);
    placeWrapper(
      wrapper,
      EDGE_GAP + position.x * availableWidth,
      EDGE_GAP + position.y * availableHeight,
      rect.width,
      rect.height
    );
  };

  const initializeButton = (button) => {
    if (initializedButtons.has(button)) return;

    const wrapper = button.closest('.activator-nudge');
    if (!wrapper) return;

    initializedButtons.add(button);
    button.style.touchAction = 'none';
    button.style.cursor = 'grab';
    button.setAttribute('aria-description', 'Drag to move this button. Activate to open chat.');
    restorePosition(wrapper, button);

    let drag = null;
    let suppressClick = false;

    button.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

      const rect = button.getBoundingClientRect();
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLeft: rect.left,
        startTop: rect.top,
        width: rect.width,
        height: rect.height,
        moved: false,
      };
      button.setPointerCapture?.(event.pointerId);
    });

    button.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;

      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) return;

      drag.moved = true;
      suppressClick = true;
      button.style.cursor = 'grabbing';
      event.preventDefault();
      placeWrapper(
        wrapper,
        drag.startLeft + deltaX,
        drag.startTop + deltaY,
        drag.width,
        drag.height
      );
    });

    const finishDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;

      if (drag.moved) {
        event.preventDefault();
        const rect = button.getBoundingClientRect();
        savePosition(rect.left, rect.top, rect.width, rect.height);
      }

      button.releasePointerCapture?.(event.pointerId);
      button.style.cursor = 'grab';
      drag = null;
    };

    button.addEventListener('pointerup', finishDrag);
    button.addEventListener('pointercancel', finishDrag);
    button.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      },
      true
    );

    window.addEventListener('resize', () => restorePosition(wrapper, button), { passive: true });
  };

  const searchRoot = (root) => {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('button[part~="activator"], button.activator').forEach(initializeButton);
    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        observeRoot(element.shadowRoot);
        searchRoot(element.shadowRoot);
      }
    });
  };

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.('button[part~="activator"], button.activator')) initializeButton(node);
          searchRoot(node);
        });
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  };

  const scan = () => searchRoot(document);
  observeRoot(document.documentElement);
  scan();

  let attempts = 0;
  const timer = window.setInterval(() => {
    scan();
    attempts += 1;
    if (attempts >= 40) window.clearInterval(timer);
  }, 500);
})();
